import { ArrowLeft } from '@orgus/ui/icons';
import { Fragment } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { IconButton, Tooltip, cn } from '@orgus/ui';
import { useCrumbs } from '../../lib/crumbs.ts';
import type { Crumb } from '../../lib/crumbs.ts';
import { navForPath } from '../../lib/nav.ts';
import { backDelta, historyIdx, readOrigin } from '../../lib/origin.ts';
import { WorkspaceSwitcher } from '../../features/teams/WorkspaceSwitcher.tsx';

const SEP = <span aria-hidden className="shrink-0 text-fg-faint">/</span>;
const CRUMB_LINK = 'min-w-0 truncate rounded-control text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg';

/**
 * "Back to <origin>" (ADR 0076): shown when this page was entered from another view. It steps
 * back through history to that very entry (its query, open drawer and scroll come back), so the
 * browser's Back and Alt+Left agree; with no index to step to, it opens the origin's address.
 */
export function BackToOrigin() {
  const location = useLocation();
  const navigate = useNavigate();
  const origin = readOrigin(location.state);
  if (!origin) return null;
  const label = `Back to ${origin.label}`;
  const go = () => {
    const delta = backDelta(origin, historyIdx());
    if (delta !== null) navigate(delta);
    else navigate(origin.href);
  };
  return (
    <>
      {/* Wide: the arrow and the origin's name, quiet; narrow: the arrow alone. */}
      <Tooltip content={label} shortcut={['alt', 'left']}>
        <Link
          to={origin.href}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            go();
          }}
          aria-label={label}
          className="hidden h-control-sm min-w-0 max-w-48 shrink-0 items-center gap-2 rounded-control px-2 text-ui text-fg-muted transition-colors duration-(--duration-fast) hover:bg-hover hover:text-fg md:flex"
        >
          <ArrowLeft className="size-4 shrink-0 text-fg-subtle" aria-hidden />
          <span className="min-w-0 truncate" title={origin.label}>
            {origin.label}
          </span>
        </Link>
      </Tooltip>
      <span className="flex md:hidden">
        <IconButton label={label} icon={<ArrowLeft />} onClick={go} />
      </span>
      <span aria-hidden className="hidden h-4 w-px shrink-0 bg-border md:block" />
    </>
  );
}

/**
 * The top bar's path (ADR 0076): workspace, the page, then the levels the page declared with
 * usePageCrumbs. Every level but the last is a link; the page's name stays the one h1. On a
 * phone only the current view shows, at full width: its parent is already named by the page's own
 * section picker or tabs, and Back (or the rail) is the way up.
 */
export function Breadcrumbs({ title }: { title: string }) {
  const { pathname } = useLocation();
  const trail = useCrumbs();
  const page = navForPath(pathname);
  // The page itself is the first level; on a phone only the last level shows.
  const n = trail.length + 1;
  const phone = (i: number) => (i < n - 1 ? 'max-md:hidden' : undefined);
  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 shrink items-center gap-2 max-md:flex-1">
      {/* Below 1024 px the page's own path needs the room, so the workspace switcher steps aside. */}
      <div className="hidden min-w-0 shrink-0 items-center gap-2 lg:flex">
        <WorkspaceSwitcher />
        {SEP}
      </div>
      <h1 className={cn('min-w-0 truncate text-body font-semibold', n > 1 && 'max-md:sr-only')}>
        {trail.length && page ? (
          <Link to={page.to} className={cn(CRUMB_LINK, 'font-medium')} title={title}>
            {title}
          </Link>
        ) : (
          <span className="text-fg" title={title}>
            {title}
          </span>
        )}
      </h1>
      {trail.map((c: Crumb, i) => {
        const level = i + 1;
        const last = level === n - 1;
        return (
          <Fragment key={`${i}:${c.label}`}>
            <span className="flex shrink-0 max-md:hidden">{SEP}</span>
            {last || !c.to ? (
              <span aria-current={last ? 'page' : undefined} title={c.label} className={cn('min-w-0 truncate text-body', last ? 'font-semibold text-fg' : 'text-fg-subtle', phone(level))}>
                {c.label}
              </span>
            ) : (
              <Link to={c.to} title={c.label} className={cn(CRUMB_LINK, 'text-body', phone(level))}>
                {c.label}
              </Link>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}
